import { useCallback, useEffect, useState } from 'react';

import { getAccessToken, refreshAccessToken } from '../lib/axios';

const EXPIRY_SKEW_MS = 60_000;

function tokenNeedsRefresh(token: string | null): boolean {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp !== 'number' || payload.exp * 1000 - Date.now() < EXPIRY_SKEW_MS;
  } catch {
    return true;
  }
}

/**
 * Access token for the chat WebSocket query string. A static value (not an async `useAgent` query)
 * keeps the hook free of React Suspense; the token is renewed after a socket close so a reconnect
 * never reuses an expired token.
 */
export function useChatSocketToken(active: boolean) {
  const [socketToken, setSocketToken] = useState<string | null>(() => getAccessToken());

  const refreshSocketToken = useCallback(async () => {
    const current = getAccessToken();
    const next = tokenNeedsRefresh(current)
      ? ((await refreshAccessToken().catch(() => null)) ?? getAccessToken())
      : current;
    setSocketToken(next);
  }, []);

  useEffect(() => {
    if (active && tokenNeedsRefresh(socketToken)) void refreshSocketToken();
  }, [active, refreshSocketToken, socketToken]);

  return { socketToken, refreshSocketToken };
}
